/**
 * TypingIndicator — three-dot animation for LLM thinking.
 */

import { useEffect, useRef } from "react";
import { Animated, StyleSheet, View } from "react-native";

import { colors, spacing } from "@/lib/theme";

export function TypingIndicator() {
    const dot1 = useRef(new Animated.Value(0)).current;
    const dot2 = useRef(new Animated.Value(0)).current;
    const dot3 = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const animate = (dot: Animated.Value, delay: number) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(dot, { toValue: 1, duration: 300, useNativeDriver: true }),
                    Animated.timing(dot, { toValue: 0, duration: 300, useNativeDriver: true })
                ])
            );

        const a1 = animate(dot1, 0);
        const a2 = animate(dot2, 200);
        const a3 = animate(dot3, 400);
        a1.start();
        a2.start();
        a3.start();

        return () => {
            a1.stop();
            a2.stop();
            a3.stop();
        };
    }, [dot1, dot2, dot3]);

    const dotStyle = (anim: Animated.Value) => ({
        transform: [
            {
                translateY: anim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -4]
                })
            }
        ],
        opacity: anim.interpolate({
            inputRange: [0, 1],
            outputRange: [0.4, 1]
        })
    });

    return (
        <View style={styles.container}>
            <View style={styles.bubble}>
                <Animated.View style={[styles.dot, dotStyle(dot1)]} />
                <Animated.View style={[styles.dot, dotStyle(dot2)]} />
                <Animated.View style={[styles.dot, dotStyle(dot3)]} />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        alignItems: "flex-start",
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs
    },
    bubble: {
        flexDirection: "row",
        gap: spacing.xs,
        backgroundColor: colors.gray100,
        borderRadius: 18,
        borderBottomLeftRadius: 4,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.textTertiary
    }
});
